import React from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, ModalBody, Spinner } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { saveAndConfirmCart } from "../../../redux/checkout/action";
import { serverResponse } from "../../../util/fakeServer";
import { checkoutIdParamUrl } from "../../../helper/constant";
import { getOrderStoreId, buildSlotPayload } from "../../../helper/checkoutStore";

function formatTime(t) {
  if (!t) return "";
  return t.substring(0, 5);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    const parts = dateStr.split("-");
    return parts[2] + "/" + parts[1] + "/" + parts[0].substring(2);
  } catch {
    return dateStr;
  }
}

function ReviewModal(props) {
  const { isOpen, toggle, checkout, groceryList } = props;
  const navigate = useNavigate();

  const {
    confirmLoading,
    orderError,
    deliveryMethod,
    shippingData,
    shippingCost,
    paymentMethod,
    selectedStore,
    pickupDate,
    selectedSlot,
    ecommerceConfig = {},
  } = checkout;

  const list = groceryList.list || [];

  const subtotal = list
    .filter((item) => !item.no_price)
    .reduce((sum, item) => sum + parseFloat(item.currentPrice || 0), 0);

  const shipping = deliveryMethod === "delivery" ? parseFloat(shippingCost || 0) : 0;
  const total = subtotal + shipping;
  const itemCount = list.reduce((sum, item) => sum + (item.quantity || 1), 0);

  // Contrassegno: informativo, NON incluso nel Totale (solo delivery + contanti).
  const codFee = Number(ecommerceConfig.codFee) || 0;
  const showCodFee =
    deliveryMethod === "delivery" && paymentMethod === "cash" && codFee > 0;

  const handleConfirm = () => {
    const storeId = getOrderStoreId(checkout);

    const items = list.map((item) => ({
      id_prodotto: item.item_id,
      quantity: item.quantity,
      price: parseFloat(item.currentPrice || item.price || 0),
    }));

    const fullOrderData = {
      id_leaflet: serverResponse.leaflet.id,
      id_param_url: localStorage.getItem(checkoutIdParamUrl) || null,
      items,
      delivery: {
        method: deliveryMethod,
        store_id: storeId,
        date: deliveryMethod === "pickup" ? (pickupDate || "") : "",
        slot: buildSlotPayload(selectedSlot),
        address:
          deliveryMethod === "delivery"
            ? {
                indirizzo: shippingData.indirizzo,
                citta: shippingData.citta,
                cap: shippingData.cap,
              }
            : null,
      },
      shipping: {
        nome: shippingData.nome,
        cognome: shippingData.cognome,
        email: shippingData.email,
        telefono: shippingData.telefono,
        indirizzo: shippingData.indirizzo || "",
        citta: shippingData.citta || "",
        cap: shippingData.cap || "",
        note: shippingData.note || "",
      },
      payment_method: paymentMethod,
      totals: {
        subtotal: parseFloat(subtotal.toFixed(2)),
        shipping_cost: parseFloat(shipping.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
      },
    };

    props.saveAndConfirmCart(fullOrderData, navigate);
  };

  const slotLabel = selectedSlot
    ? formatTime(selectedSlot.time_from) + " - " + formatTime(selectedSlot.time_to)
    : "";

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="up-modal--sm up-review-dialog"
      contentClassName="up-review-content"
    >
      <ModalBody className="up-modal__body">
        <div className="up-modal__header">
          <h3 className="up-modal__title">Conferma ordine</h3>
          <div className="up-modal__close" onClick={toggle}>
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>

        <div style={{ padding: "18px 22px 22px", display: "flex", flexDirection: "column", minHeight: 0, flex: "1 1 auto", overflow: "hidden" }}>
          {/* Error state */}
          {orderError && (
            <div style={{
              background: "#fff5f5",
              border: "1px solid #fce4e4",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: "0.88rem",
              color: "#dc3545",
              marginBottom: 16,
            }}>
              {orderError}
            </div>
          )}

          {/* Products summary */}
          <div className="up-review-products" style={{ marginBottom: 16 }}>
            {list.map((item, idx) => (
              <div
                key={item.item_id + "-" + (item.variety || idx)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #f5f5f5",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#222" }}>
                    {item.field1 || item.description || "Prodotto"}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#888" }}>
                    Qtà: {item.quantity} {item.grocery_label || ""}
                  </div>
                </div>
                <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#222", marginLeft: 10 }}>
                  {!item.no_price ? "€ " + parseFloat(item.currentPrice || 0).toFixed(2) : "-"}
                </div>
              </div>
            ))}
          </div>

          {/* Delivery info */}
          <div style={{
            background: "#fafafa",
            borderRadius: 10,
            padding: "12px 14px",
            marginBottom: 16,
            fontSize: "0.85rem",
            color: "#555",
          }}>
            {deliveryMethod === "pickup" && (
              <>
                <div><strong>Ritiro:</strong> {selectedStore?.name || selectedStore?.nome}</div>
                <div><strong>Data:</strong> {formatDate(pickupDate)}</div>
                {slotLabel && <div><strong>Orario:</strong> {slotLabel}</div>}
              </>
            )}
            {deliveryMethod === "delivery" && (
              <>
                <div><strong>Consegna:</strong> {shippingData.indirizzo}, {shippingData.cap} {shippingData.citta}</div>
              </>
            )}
            <div style={{ marginTop: 4 }}>
              <strong>A:</strong> {shippingData.nome} {shippingData.cognome} — {shippingData.telefono}
            </div>
          </div>

          {/* Totals */}
          <div className="co-summary">
            <div className="co-summary__row">
              <span>Subtotale ({itemCount} prodotti)</span>
              <span>€ {subtotal.toFixed(2)}</span>
            </div>
            {deliveryMethod === "delivery" && (
              <div className="co-summary__row">
                <span>Spedizione</span>
                <span style={{ color: shipping === 0 ? "#28a745" : "#222" }}>
                  {shipping === 0 ? "Gratuita" : "€ " + shipping.toFixed(2)}
                </span>
              </div>
            )}
            <div className="co-summary__row co-summary__total">
              <span>Totale</span>
              <span>€ {total.toFixed(2)}</span>
            </div>
            {/* Contrassegno: informativo, NON nel Totale (pagato in contanti alla consegna) */}
            {showCodFee && (
              <div
                className="co-summary__row co-summary__note"
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: "1px dashed #e0d6b0",
                  fontSize: "0.82rem",
                  color: "#7a5b00",
                }}
              >
                <span>+ Contrassegno (in contanti alla consegna)</span>
                <span>€ {codFee.toFixed(2)}</span>
              </div>
            )}
          </div>

          {paymentMethod === "card" && (
            <div style={{
              marginTop: 12,
              padding: "10px 12px",
              backgroundColor: "#f0f7ff",
              borderRadius: 8,
              fontSize: "0.82rem",
              color: "#4a6fa5",
              lineHeight: 1.5,
            }}>
              <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 6 }} />
              Sulla carta verrà trattenuta una preautorizzazione di <strong>€ {(total * 1.1).toFixed(2)}</strong> (totale + 10%).
              L'importo effettivo addebitato sarà <strong>€ {total.toFixed(2)}</strong>.
            </div>
          )}

          {/* Actions */}
          <div className="up-modal__footer" style={{ padding: "18px 0 0" }}>
            <button className="up-btn up-btn--ghost" onClick={toggle} disabled={confirmLoading}>
              Annulla
            </button>
            <button
              className="up-btn up-btn--primary"
              onClick={handleConfirm}
              disabled={confirmLoading}
              style={confirmLoading ? { opacity: 0.6 } : {}}
            >
              {confirmLoading ? (
                <>
                  <Spinner size="sm" style={{ marginRight: 6 }} />
                  Invio...
                </>
              ) : (
                "Conferma ordine"
              )}
            </button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}

const mapStateToProps = (state) => ({
  checkout: state.checkout,
  groceryList: state.groceryList,
});

export default connect(mapStateToProps, { saveAndConfirmCart })(ReviewModal);
